import { useEffect } from "react"
import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import KGBanner from "@/common/assets/image/walletAsAService/showCase/hero&banner/KG Banner.webp"
import YGGJBanner from "@/common/assets/image/walletAsAService/showCase/hero&banner/YGGJ Banner.webp"
import TAGBanner from "@/common/assets/image/walletAsAService/showCase/hero&banner/TAG Banner.webp"

import WalletBuilderDark from "@/common/assets/image/products/icons/WalletBuilderDark.svg"
import Swiper1 from "@/common/assets/image/products/wallet/walletBuilder/Swiper1.webp"
import Swiper2 from "@/common/assets/image/products/wallet/walletBuilder/Swiper2.webp"
import Swiper3 from "@/common/assets/image/products/wallet/walletBuilder/Swiper3.webp"
import Swiper4 from "@/common/assets/image/products/wallet/walletBuilder/Swiper4.webp"
import Swiper5 from "@/common/assets/image/products/wallet/walletBuilder/Swiper5.webp"
import Swiper6 from "@/common/assets/image/products/wallet/walletBuilder/Swiper6.webp"
import Swiper7 from "@/common/assets/image/products/wallet/walletBuilder/Swiper7.webp"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import Statistic from "@/common/components/Statistic"
import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import Description from "@/common/components/Description"
import ProductCTA from "@/common/components/ProductCTA"
import ProductViewMore from "@/common/components/ProductViewMore"
import { ProductBanner } from "@/common/components/ProductBanner"
import { ProductNames, SolutionNames } from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import NextImg from "@/common/components/NextImg"
import DataSwiper from "@/common/components/DataSwiper"
import ShowCase from "@/common/components/ShowCase"

const WalletBuilderPage = () => {
  const { t } = useTranslation(["walletBuilder"])
  const { t: commonT } = useTranslation(["common"])

  useEffect(() => {
    setTimeout(() => {
      if (window.location.hash === "#wallet-builder-showcase") {
        const element = document.getElementById("wallet-builder-showcase")
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      }
    }, 1000)
  }, [])

  return (
    <>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/products/compliance-pro"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/products/compliance-pro"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/products/compliance-pro"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/products/compliance-pro"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          icon={WalletBuilderDark.src}
          bannerType={ProductNames.WalletBuilder}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
        />
        <SectionLine />
        <Description
          id="wallet-builder-desc-1"
          items={[
            {
              wide: true,
              titles: [{ text: t("Desc.1.title"), textType: "h2" }],
              checkList: [
                {
                  list: [
                    {
                      description: t("Desc.1.checkList.0")
                    },
                    {
                      description: t("Desc.1.checkList.1")
                    },
                    {
                      description: t("Desc.1.checkList.2")
                    }
                  ]
                },
                {
                  list: [
                    {
                      description: t("Desc.1.checkList.3")
                    },
                    {
                      description: t("Desc.1.checkList.4")
                    }
                  ]
                }
              ]
            }
          ]}
        />
        <Description
          id="wallet-builder-desc-2"
          backgroundType="none"
          items={[
            {
              titles: [{ text: t("Desc.2.title") }],
              content: t("Desc.2.content")
            },
            {
              titles: [{ text: t("Desc.3.title") }],
              content: t("Desc.3.content")
            },
            {
              titles: [{ text: t("Desc.4.title") }],
              content: t("Desc.4.content")
            },
            {
              titles: [{ text: t("Desc.5.title") }],
              content: t("Desc.5.content")
            },
            {
              titles: [{ text: t("Desc.6.title") }],
              content: t("Desc.6.content")
            }
          ]}
        />
        <Statistic
          id="wallet-builder-statistic"
          title={t("Statistic.title")}
          items={[
            {
              number: 1,
              numberSuffix: t("Statistic.items.0.numberSuffix"),
              description: t("Statistic.items.0.description"),
              trend: "smaller"
            },
            {
              number: 8,
              numberSuffix: t("Statistic.items.1.numberSuffix"),
              description: t("Statistic.items.1.description"),
              trend: "bigger"
            },
            {
              number: 90,
              numberSuffix: t("Statistic.items.2.numberSuffix"),
              description: t("Statistic.items.2.description"),
              trend: "bigger"
            },
            {
              number: 100,
              numberSuffix: t("Statistic.items.3.numberSuffix"),
              description: t("Statistic.items.3.description"),
              trend: "bigger"
            }
          ]}
          buttonText={t("Statistic.button")}
          buttonHref="https://app.kryptogo.com/compliance/signup"
        />
        <ProductBanner
          short
          paddingTop="3vh"
          titleh2={t("Banner.2.title")}
          description={t("Banner.2.description")}
        />
        <DataSwiper
          items={[
            {
              titleh3: t("swiper.items.0.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper1.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.0.checkList.0.title"),
                      description: t("swiper.items.0.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.0.checkList.1.title"),
                      description: t("swiper.items.0.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.0.checkList.2.title"),
                      description: t("swiper.items.0.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.1.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper2.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.1.checkList.0.title"),
                      description: t("swiper.items.1.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.1.checkList.1.title"),
                      description: t("swiper.items.1.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.1.checkList.2.title"),
                      description: t("swiper.items.1.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.2.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper3.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.2.checkList.0.title"),
                      description: t("swiper.items.2.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.2.checkList.1.title"),
                      description: t("swiper.items.2.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.2.checkList.2.title"),
                      description: t("swiper.items.2.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.3.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper4.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.3.checkList.0.title"),
                      description: t("swiper.items.3.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.3.checkList.1.title"),
                      description: t("swiper.items.3.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.3.checkList.2.title"),
                      description: t("swiper.items.3.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.4.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper5.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.4.checkList.0.title"),
                      description: t("swiper.items.4.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.4.checkList.1.title"),
                      description: t("swiper.items.4.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.4.checkList.2.title"),
                      description: t("swiper.items.4.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.5.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper6.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.5.checkList.0.title"),
                      description: t("swiper.items.5.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.5.checkList.1.title"),
                      description: t("swiper.items.5.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.5.checkList.2.title"),
                      description: t("swiper.items.5.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.6.title"),
              titleh3Type: "normal",
              comp2: <NextImg src={Swiper7.src} />,
              checkList: [
                {
                  listColor: "disabled",
                  list: [
                    {
                      title: t("swiper.items.6.checkList.0.title"),
                      description: t("swiper.items.6.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.6.checkList.1.title"),
                      description: t("swiper.items.6.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.6.checkList.2.title"),
                      description: t("swiper.items.6.checkList.2.description")
                    }
                  ]
                }
              ]
            }
          ]}
        />
        <ShowCase
          id="wallet-builder-showcase"
          title={t("showcase.title")}
          data={[
            {
              img: KGBanner,
              link: "/use-case/kryptogo-wallet",
              title: t("showcase.kg.title"),
              desc: t("showcase.kg.description"),
              tags: ["Hot", "Wallet", "News"],
              date: "2023/05/29"
            },
            {
              img: YGGJBanner,
              link: "/use-case/stickey-wallet",
              title: t("showcase.stickey.title"),
              desc: t("showcase.stickey.description"),
              tags: ["Hot", "Wallet", "News"],
              date: "2023/05/29"
            },
            {
              img: TAGBanner,
              link: "/use-case/tag-wallet",
              title: t("showcase.tag.title"),
              desc: t("showcase.tag.description"),
              tags: ["Hot", "Wallet", "News"],
              date: "2023/05/29"
            }
          ]}
        />
        <ProductCTA
          id="wallet-builder-cta"
          title={t("CTA.title")}
          desc={t("CTA.desc")}
          primaryButton={{ text: t("CTA.button") }}
        />
        <ProductViewMore
          commonT={commonT}
          title={commonT("ViewMore.title")}
          tab={[
            {
              name: commonT("ViewMore.products"),
              content: [
                ProductNames.WalletApplication,
                ProductNames.User360,
                ProductNames.AssetPro
              ]
            },
            {
              name: commonT("ViewMore.solutions"),
              content: [SolutionNames.GameFi, SolutionNames.Defi]
            }
          ]}
          actionText={commonT("ViewMore.allProducts")}
        ></ProductViewMore>
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, [
      "common",
      "walletBuilder",
      "contactUs",
      "walletAsAService"
    ]))
  }
})

export default WalletBuilderPage
